import { useState, useCallback, useEffect } from 'react';
import imageCompression from 'browser-image-compression';

export interface UseMultiImageUploaderOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  quality?: number;
  maxFiles?: number;
  onBeforeCompress?: (file: File) => void;
  onCompressSuccess?: (compressedFiles: File[]) => void;
  onCompressError?: (error: Error) => void;
}

type Preview = {
  url: string;
  id: number; // Para una key estable en React
};

export const useMultiImageUploader = (options: UseMultiImageUploaderOptions = {}) => {
  const {
    maxSizeMB = 1,
    maxWidthOrHeight = 1920,
    quality = 0.8,
    maxFiles = 10,
    onBeforeCompress,
    onCompressSuccess,
    onCompressError,
  } = options;

  const [previews, setPreviews] = useState<Preview[]>([]);
  const [compressedFiles, setCompressedFiles] = useState<File[]>([]);
  const [isCompressing, setIsCompressing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.url));
    };
  }, [previews]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const totalFiles = compressedFiles.length + files.length;
      if (totalFiles > maxFiles) {
        setError(`No puedes subir más de ${maxFiles} imágenes. Has intentado subir ${files.length} pero ya tienes ${compressedFiles.length}.`);
        e.target.value = ''; // Limpiar el input
        return;
      }

      setIsCompressing(true);
      setError(null);

      const newCompressedFiles: File[] = [];
      const newPreviews: Preview[] = [];

      const compressionPromises = Array.from(files).map(async (file, index) => {
        if (!file.type.startsWith('image/')) {
          console.warn(`El archivo '${file.name}' no es una imagen y será omitido.`);
          return;
        }

        onBeforeCompress?.(file);

        try {
          const compressed = await imageCompression(file, {
            maxSizeMB,
            maxWidthOrHeight,
            useWebWorker: true,
            fileType: 'image/webp',
            initialQuality: quality,
          });

          newCompressedFiles.push(compressed);
          newPreviews.push({
            url: URL.createObjectURL(compressed),
            id: Date.now() + index,
          });
        } catch (err: any) {
          console.error(`Error comprimiendo ${file.name}:`, err);
          setError(`Error al procesar la imagen ${file.name}.`);
          onCompressError?.(err);
        }
      });

      await Promise.all(compressionPromises);

      const updatedFiles = [...compressedFiles, ...newCompressedFiles];
      setCompressedFiles(updatedFiles);
      setPreviews(prev => [...prev, ...newPreviews]);

      onCompressSuccess?.(updatedFiles);
      setIsCompressing(false);
      e.target.value = ''; // Limpiar el input para permitir volver a seleccionar el mismo archivo
    },
    [compressedFiles, maxFiles, maxSizeMB, maxWidthOrHeight, quality, onBeforeCompress, onCompressSuccess, onCompressError]
  );

  const removeImage = useCallback((index: number) => {
    setCompressedFiles(current => {
      return current.filter((_, i) => i !== index);
    });
    setPreviews(current => {
      const updated = [...current];
      const removed = updated.splice(index, 1);
      URL.revokeObjectURL(removed[0].url);
      return updated;
    });
  }, []);

  const reset = useCallback(() => {
    previews.forEach(preview => URL.revokeObjectURL(preview.url));
    setCompressedFiles([]);
    setPreviews([]);
    setError(null);
  }, [previews]);

  return {
    compressedFiles,
    previews,
    isCompressing,
    error,
    handleFileChange,
    removeImage,
    reset,
  };
};